<template>
  <v-dialog :value="value" max-width="520px" @input="$emit('input', $event)">
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-bold">{{ title }}</span>
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-caption grey--text text--darken-1 mb-1">Название</div>
        <v-text-field
          v-model="form.name"
          outlined
          dense
          :placeholder="namePlaceholder"
          hint="Необязательно. Если не заполнить, покажем имя эксперта или название типа услуги."
          class="mb-1"
        />

        <template v-if="form.kind === 'expert'">
          <div class="text-caption grey--text text--darken-1 mb-1">Эксперт</div>
          <v-select
            v-model="form.expert"
            :items="expertOptions"
            :disabled="isEditing || lockedExpertId !== null || presetMainId !== null"
            outlined
            dense
            :hint="isEditing ? 'Эксперта ссылки изменить нельзя — для другого эксперта создайте новую ссылку' : ''"
            :persistent-hint="isEditing"
            placeholder="Выберите эксперта"
            class="mb-3"
            @change="form.selected = []"
          />
        </template>
        <template v-else>
          <div class="text-caption grey--text text--darken-1 mb-1">Тип услуги</div>
          <v-select
            v-model="form.service_type"
            :items="serviceTypeOptions"
            :disabled="isEditing || presetMainId !== null"
            outlined
            dense
            :hint="isEditing ? 'Тип услуги ссылки изменить нельзя — для другого типа создайте новую ссылку' : ''"
            :persistent-hint="isEditing"
            placeholder="Выберите тип услуги"
            class="mb-3"
            @change="form.selected = []"
          />
        </template>

        <div class="text-caption grey--text text--darken-1 mb-1">{{ selectionTitle }}</div>
        <v-radio-group v-model="form.mode" row dense hide-details class="mt-0 mb-3">
          <v-radio :label="allLabel" value="all" color="#483CB9" />
          <v-radio label="Только выбранные" value="selected" color="#483CB9" />
        </v-radio-group>

        <v-autocomplete
          v-if="form.mode === 'selected'"
          v-model="form.selected"
          :items="selectionOptions"
          :disabled="!mainObjectId"
          :placeholder="mainObjectId ? 'Выберите варианты' : mainObjectPlaceholder"
          :no-data-text="noOptionsText"
          multiple
          outlined
          dense
        >
          <!-- Свой чип: длинное название обрезается многоточием, крестик удаления остаётся виден. -->
          <template #selection="{ item, attrs, parent }">
            <v-chip
              v-bind="attrs"
              small
              close
              class="public-link-form__chip"
              :title="item.text"
              @click:close="parent.selectItem(item)"
            >
              <span class="text-truncate">{{ item.text }}</span>
            </v-chip>
          </template>
        </v-autocomplete>
        <div v-else class="text-caption grey--text">
          {{ allHint }}
        </div>

        <v-alert v-if="errorMessages.length" type="error" dense text rounded="lg" class="mt-3 mb-0">
          <div v-for="(message, index) in errorMessages" :key="index">{{ message }}</div>
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn outlined color="#483CB9" class="flex-grow-1" @click="$emit('input', false)">Отмена</v-btn>
        <v-btn
          color="#483CB9"
          :dark="canSave"
          class="flex-grow-1"
          :loading="loading"
          :disabled="!canSave"
          @click="save"
        >
          {{ isEditing ? 'Сохранить' : 'Создать' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  expertName,
  expertLabel,
  serviceTypeLabel,
  serviceLabel,
  isServiceAvailable,
  isExpertAvailableForType,
} from './publicLinkLabels';

// Форма ограниченной ссылки. Основной объект (эксперт или тип услуги) после создания
// неизменяем. Пустая выборка на бэке = «все», поэтому режим «Только выбранные» без
// выбранных вариантов не сохраняем — иначе ссылка молча станет «все».
export default {
  name: 'PublicLinkFormDialog',
  props: {
    value: { type: Boolean, default: false },
    kind: { type: String, default: 'expert' }, // 'expert' | 'service'
    link: { type: Object, default: null }, // редактируемая ссылка
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    // Эксперт управляет только ссылками на себя; null — админ, выбор свободный.
    lockedExpertId: { type: Number, default: null },
    // Типы услуг, на которые пользователь может создавать ссылки; null — все.
    allowedServiceTypeIds: { type: Array, default: null },
    // Эксперт или тип услуги, из карточки которого создаётся ссылка; null — выбор в форме.
    presetMainId: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] }, // Object.entries ответа 400
  },
  data() {
    return { form: this.buildForm() };
  },
  computed: {
    isEditing() {
      return Boolean(this.link);
    },
    title() {
      const what = this.form.kind === 'expert' ? 'на эксперта' : 'на тип услуги';
      return this.isEditing ? `Ссылка ${what}` : `Новая ссылка ${what}`;
    },
    mainObjectId() {
      return this.form.kind === 'expert' ? this.form.expert : this.form.service_type;
    },
    namePlaceholder() {
      if (this.form.kind === 'expert') {
        return this.form.expert ? expertLabel(this.experts, this.form.expert) : 'Например, «Консультация для партнёров»';
      }
      return this.form.service_type
        ? serviceTypeLabel(this.serviceTypes, this.form.service_type)
        : 'Например, «Диагностика для корп. клиентов»';
    },
    mainObjectPlaceholder() {
      return this.form.kind === 'expert' ? 'Сначала выберите эксперта' : 'Сначала выберите тип услуги';
    },
    selectionTitle() {
      return this.form.kind === 'expert' ? 'Услуги эксперта' : 'Эксперты';
    },
    allLabel() {
      return this.form.kind === 'expert' ? 'Все услуги' : 'Все эксперты';
    },
    allHint() {
      return this.form.kind === 'expert'
        ? 'Гость увидит все активные услуги эксперта, включая добавленные позже.'
        : 'Гость увидит всех экспертов, оказывающих эту услугу, включая добавленных позже.';
    },
    noOptionsText() {
      return this.form.kind === 'expert' ? 'У эксперта нет активных услуг' : 'Нет экспертов, оказывающих эту услугу';
    },
    expertOptions() {
      const options = this.experts
        .filter((expert) => expert.is_active !== false
          && (this.lockedExpertId === null || expert.id === this.lockedExpertId))
        .map((expert) => ({ value: expert.id, text: expertName(expert) }));
      return this.withCurrent(options, this.form.expert, () => expertLabel(this.experts, this.form.expert));
    },
    serviceTypeOptions() {
      const options = this.serviceTypes
        .filter((item) => item.is_active !== false
          && (this.allowedServiceTypeIds === null || this.allowedServiceTypeIds.includes(item.id)))
        .map((item) => ({ value: item.id, text: item.name }));
      return this.withCurrent(
        options,
        this.form.service_type,
        () => serviceTypeLabel(this.serviceTypes, this.form.service_type),
      );
    },
    availableSelectionIds() {
      if (!this.mainObjectId) return [];
      if (this.form.kind === 'expert') {
        return this.services
          .filter((item) => item.expert === this.form.expert && isServiceAvailable(this.services, item.id))
          .map((item) => item.id);
      }
      return this.experts
        .filter((expert) => isExpertAvailableForType(this.experts, this.services, expert.id, this.form.service_type))
        .map((expert) => expert.id);
    },
    // Доступные варианты + уже выбранные, но ставшие недоступными: их можно оставить или
    // убрать, но не добавить заново (бэк вернёт ошибку) — после удаления из выбора они пропадают.
    selectionOptions() {
      const label = (id) => (this.form.kind === 'expert'
        ? serviceLabel(this.services, this.serviceTypes, id)
        : expertLabel(this.experts, id));
      const available = this.availableSelectionIds.map((id) => ({ value: id, text: label(id) }));
      const unavailable = this.form.selected
        .filter((id) => !this.availableSelectionIds.includes(id))
        .map((id) => ({ value: id, text: `${label(id)} — недоступно` }));
      return [...available, ...unavailable];
    },
    canSave() {
      if (!this.mainObjectId) return false;
      return this.form.mode === 'all' || this.form.selected.length > 0;
    },
    errorMessages() {
      return this.errors.reduce((acc, [, messages]) => acc.concat(messages), []);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.form = this.buildForm();
    },
  },
  methods: {
    buildForm() {
      if (this.link) {
        const isExpertLink = this.kind === 'expert';
        const selected = (isExpertLink ? this.link.services : this.link.experts) || [];
        return {
          kind: this.kind,
          name: this.link.name || '',
          expert: isExpertLink ? this.link.expert : null,
          service_type: isExpertLink ? null : this.link.service_type,
          mode: selected.length ? 'selected' : 'all',
          selected: [...selected],
        };
      }
      const isExpertLink = this.kind === 'expert';
      return {
        kind: this.kind,
        name: '',
        expert: isExpertLink && this.presetMainId !== null ? this.presetMainId : this.lockedExpertId,
        service_type: isExpertLink ? null : this.presetMainId,
        mode: 'all',
        selected: [],
      };
    },
    // У редактируемой ссылки основной объект мог стать неактивным — показываем его в
    // заблокированном поле, а не пустое значение.
    withCurrent(options, currentId, labelOf) {
      if (!currentId || options.some((option) => option.value === currentId)) return options;
      return [...options, { value: currentId, text: `${labelOf()} — недоступно` }];
    },
    save() {
      if (!this.canSave) return;
      const selection = this.form.mode === 'all' ? [] : this.form.selected;
      const name = this.form.name.trim();
      let payload;
      if (this.form.kind === 'expert') {
        payload = this.isEditing
          ? { name, services: selection }
          : { name, expert: this.form.expert, services: selection };
      } else {
        payload = this.isEditing
          ? { name, experts: selection }
          : { name, service_type: this.form.service_type, experts: selection };
      }
      this.$emit('save', { kind: this.form.kind, id: this.isEditing ? this.link.id : null, payload });
    },
  },
};
</script>

<style lang="scss" scoped>
.public-link-form__chip {
  max-width: 100%;
  margin: 2px 4px 2px 0;

  ::v-deep .v-chip__content {
    min-width: 0;
    max-width: 100%;
  }
}
</style>
